/**
 * Package quote store + overview popup.
 *
 * Public API (window.RQ static methods):
 *   RQ.getQuote() / RQ.getItems() / RQ.getItemCount() / RQ.getSubtotal()
 *   RQ.addItem(item) / RQ.addItemFromForm(form, meta) / RQ.updateItemFromForm(key, form, meta)
 *   RQ.displayedFormPrice(form, variantPrice, productId) / RQ.formatMoney(cents)
 *   RQ.removeItem(key) / RQ.updateQuantity(key, quantity) / RQ.updateItem(key, patch) / RQ.clearItems()
 *   RQ.hasProduct(productId) / RQ.importCart(cart, { replace })
 *   RQ.markStarted(route) / RQ.clearStarted() / RQ.isStarted()
 *   RQ.show() / RQ.close() / RQ.toggle() / RQ.isOpen()
 *   RQ.refresh() / RQ.syncTriggers() / RQ.init()
 */
class RQ {
  static STORAGE_KEY = 'austpek-rfq-quote';
  static STARTED_KEY = 'austpek-rfq-started';
  static ROUTE_KEY = 'austpek-rfq-route';
  static PANEL_KEY = 'austpek-rfq-panel';
  static OPEN_CLASS = 'is-open';
  static FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  static #root = null;
  static #lastFocus = null;
  static #bound = false;
  static #syncTimer = 0;
  static #bcpoByHandle = new Map();
  static #bcpoByProductId = new Map();

  static #emptyQuote() {
    return {
      version: 1,
      started: false,
      route: '',
      panel: '',
      items: [],
      updatedAt: null
    };
  }

  static #readStorage() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY) || sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return this.#emptyQuote();
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return this.#emptyQuote();
      return {
        ...this.#emptyQuote(),
        ...parsed,
        items: parsed.items.map((item) => this.#normalizeItem(item))
      };
    } catch (error) {
      return this.#emptyQuote();
    }
  }

  static #save(quote) {
    const next = {
      ...this.#emptyQuote(),
      ...quote,
      items: Array.isArray(quote.items) ? quote.items : [],
      updatedAt: new Date().toISOString()
    };
    const payload = JSON.stringify(next);
    try {
      localStorage.setItem(this.STORAGE_KEY, payload);
    } catch (error) {
      // Fall through to sessionStorage.
    }
    try {
      sessionStorage.setItem(this.STORAGE_KEY, payload);
    } catch (error) {
      // Storage may be unavailable.
    }
    return next;
  }

  static #emit(name, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail
      })
    );
  }

  static #itemImage(raw = {}) {
    if (typeof raw.image === 'string' && raw.image) return raw.image;
    if (raw.image?.src) return raw.image.src;
    if (raw.image?.url) return raw.image.url;
    if (typeof raw.featured_image === 'string' && raw.featured_image) return raw.featured_image;
    if (raw.featured_image?.url) return raw.featured_image.url;
    if (raw.featured_image?.src) return raw.featured_image.src;
    return '';
  }

  static #fingerprint(variantId, properties) {
    const props = properties && typeof properties === 'object' ? properties : {};
    const keys = Object.keys(props).sort();
    const serialized = keys.map((key) => `${key}=${String(props[key])}`).join('|');
    return `${variantId}:${serialized}`;
  }

  static #sameLine(left, right) {
    return this.#fingerprint(left.variant_id || left.id, left.properties) === this.#fingerprint(right.variant_id || right.id, right.properties);
  }

  static #normalizeItem(raw = {}) {
    const quantity = Math.max(1, Number(raw.quantity) || 1);
    const optionPrice = Number(raw.option_price) || 0;
    const price = Number(raw.price || raw.original_price || 0) || 0;
    const originalPrice = Number(raw.original_price || 0) || Math.max(0, price - optionPrice);
    const variantId = Number(raw.variant_id || raw.id) || 0;
    const properties = raw.properties && typeof raw.properties === 'object' ? { ...raw.properties } : {};
    const computedLine = price * quantity;
    // Shopify cart.js `final_line_price` is variant-only. Once VO extras are known,
    // recompute the line total so "Use my cart" matches the Add products flow.
    const linePrice = optionPrice
      ? computedLine
      : Number(raw.final_line_price || raw.line_price || computedLine) || computedLine;
    const key = raw.key || `rq-${this.#fingerprint(variantId, properties)}`;
    return {
      key,
      id: variantId,
      variant_id: variantId,
      product_id: Number(raw.product_id) || 0,
      product_title: raw.product_title || raw.title || '',
      variant_title: raw.variant_title && raw.variant_title !== 'Default Title' ? raw.variant_title : '',
      title: raw.title || raw.product_title || '',
      quantity,
      price,
      original_price: originalPrice || price,
      option_price: optionPrice,
      line_price: linePrice,
      final_line_price: linePrice,
      image: this.#itemImage(raw),
      url: raw.url || '',
      sku: raw.sku || '',
      handle: raw.handle || '',
      vendor: raw.vendor || '',
      properties,
      source: raw.source || 'quote',
      unavailable: Boolean(raw.unavailable)
    };
  }

  static #label(key) {
    return (this.#root && this.#root.dataset[key]) || '';
  }

  static #escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static #formatMoney(cents) {
    const format = this.#label('moneyFormat') || window.theme?.moneyFormat || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (error) {
        // Fall through to the local formatter.
      }
    }
    const amountCents = Number(cents);
    const safeCents = Number.isFinite(amountCents) ? amountCents : 0;
    const tokenMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const token = tokenMatch ? tokenMatch[1] : 'amount';
    const delimiters = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.']
    };
    const [precision, thousands, decimal] = delimiters[token] || [2, ',', '.'];
    const value = (Math.abs(safeCents) / 100).toFixed(precision);
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const formatted = parts.length > 1 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    const signed = safeCents < 0 ? `-${formatted}` : formatted;
    return format.replace(/\{\{\s*\w+\s*\}\}/, signed);
  }

  static #itemMeta(item) {
    const props = item.properties || {};
    const values = Object.keys(props)
      .filter((key) => key && key.charAt(0) !== '_' && props[key] != null && String(props[key]).trim() !== '')
      .map((key) => String(props[key]).trim())
      .filter((value) => !/^https?:\/\//i.test(value));
    if (values.length) return values.join(' · ');
    if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
    return '';
  }

  static #panelToStep(panel) {
    if (panel === 'project') return 2;
    if (panel === 'contact') return 3;
    return 1;
  }

  static #routesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  static async #fetchCart() {
    const response = await fetch(`${this.#routesRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  static async #fetchProduct(handle) {
    if (!handle) return null;
    const response = await fetch(`${this.#routesRoot()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    return response.json();
  }

  static #findVariant(product, variantId) {
    if (!product?.variants) return null;
    for (const variant of product.variants) {
      if (String(variant.id) === String(variantId)) return variant;
    }
    return product.variants[0] || null;
  }

  static #variantImage(variant, product) {
    if (variant?.featured_image?.src) return variant.featured_image.src;
    if (typeof product?.featured_image === 'string') return product.featured_image;
    if (product?.featured_image?.src) return product.featured_image.src;
    return '';
  }

  /**
   * Product form plus VO `#bcpo`, which on PDPs can sit outside the submitted form.
   * @param {HTMLFormElement | null} form
   * @returns {ParentNode[]}
   */
  static #optionContextRoots(form) {
    const roots = [];
    const seen = new Set();
    const add = (node) => {
      if (!node || seen.has(node)) return;
      seen.add(node);
      roots.push(node);
    };
    add(form);
    if (form) {
      add(form.closest('product-form'));
      add(form.closest('[data-apq-quote-product]'));
      const globalBcpo = document.getElementById('bcpo');
      const globalQuickview = document.querySelector('.bcpo-quickview');
      if (globalBcpo && !form.contains(globalBcpo)) add(globalBcpo);
      if (globalQuickview && !form.contains(globalQuickview)) add(globalQuickview);
    }
    return roots;
  }

  /**
   * The product form whose current selection a quote button represents. PDPs can render the
   * button outside the form the VO widget writes into, so fall back the same way the buy
   * buttons do rather than reading the button's own server-rendered variant attribute.
   * @param {HTMLElement} button
   * @returns {HTMLFormElement | null}
   */
  static #quoteButtonForm(button) {
    const nested = button.closest('form[action*="/cart/add"]');
    if (nested) return nested;
    const bcpo = document.getElementById('bcpo');
    const bcpoForm = bcpo && bcpo.closest('form[action*="/cart/add"]');
    if (bcpoForm) return bcpoForm;
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    return forms.length === 1 ? forms[0] : null;
  }

  /**
   * @param {Record<string, string>} properties
   * @param {string} name
   * @param {unknown} value
   */
  static #assignProperty(properties, name, value) {
    const next = String(value == null ? '' : value).trim();
    if (!next) return;
    properties[name] = properties[name] ? `${properties[name]}, ${next}` : next;
  }

  /**
   * @param {ParentNode | null} root
   * @param {Record<string, string>} properties
   * @param {Set<Element>} seen
   */
  static #collectPropertiesFromRoot(root, properties, seen) {
    if (!root || !root.querySelectorAll) return;
    for (const field of root.querySelectorAll('[name^="properties"]')) {
      if (seen.has(field) || field.disabled) continue;
      seen.add(field);
      const match = String(field.name).match(/^properties\[(.+?)\](?:\[\])?$/);
      if (!match) continue;
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
      const value = field.value;
      if (value == null || String(value).trim() === '') continue;
      this.#assignProperty(properties, match[1], value);
    }
  }

  static #parseForm(form) {
    const formData = form ? new FormData(form) : new FormData();
    const variantId = Number(formData.get('id')) || 0;
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const properties = {};
    const seenFields = new Set();
    for (const root of this.#optionContextRoots(form)) {
      this.#collectPropertiesFromRoot(root, properties, seenFields);
    }
    if (!Object.keys(properties).length) {
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+?)\](?:\[\])?$/);
        if (!match) continue;
        if (value == null || String(value).trim() === '') continue;
        this.#assignProperty(properties, match[1], value);
      }
    }
    return { variantId, quantity, properties };
  }

  /**
   * Convert a VO option dollar amount to Shopify cents.
   * @param {unknown} raw
   * @param {boolean} [convertCurrency]
   * @returns {number}
   */
  static #optionDollarsToCents(raw, convertCurrency = false) {
    if (raw == null || raw === '') return 0;
    let dollars = Number(raw);
    if (!Number.isFinite(dollars) || dollars === 0) return 0;
    if (convertCurrency) {
      const rate = Number(window.Shopify?.currency?.rate);
      if (Number.isFinite(rate) && rate !== 0 && rate !== 1) dollars *= rate;
    }
    return Math.round(dollars * 100);
  }

  /**
   * Sum VO `data-price` values (dollars) on selected options in the form and `#bcpo`.
   * @param {HTMLFormElement | null} form
   * @returns {number} cents
   */
  static #optionPriceFromForm(form) {
    let dollars = 0;
    const seen = new Set();
    const isVariantPriceField = (field) => {
      if (!field) return false;
      if (field.getAttribute('name') === 'id') return true;
      return Boolean(field.closest('select[name="id"]'));
    };
    const addField = (field) => {
      if (!field || seen.has(field) || field.disabled || isVariantPriceField(field)) return;
      seen.add(field);
      dollars += Number(field.getAttribute('data-price')) || Number(field.dataset?.price) || 0;
    };

    for (const root of this.#optionContextRoots(form)) {
      if (!root.querySelectorAll) continue;
      for (const field of root.querySelectorAll('input[data-price]:checked, option:checked[data-price]')) {
        addField(field);
      }
      for (const field of root.querySelectorAll(
        'textarea[data-price], input[type="text"][data-price], input[type="number"][data-price], input[type="file"][data-price]'
      )) {
        if (!String(field.value || '').trim()) continue;
        addField(field);
      }
    }
    return this.#optionDollarsToCents(dollars);
  }

  /**
   * Parse a money token such as `$445.00`, `445,00`, or `445` into dollars.
   * @param {string} raw
   * @returns {number}
   */
  static #parseMoneyAmount(raw) {
    let amount = String(raw == null ? '' : raw).trim();
    if (!amount) return 0;
    const commaDecimals = /,\d\d$/.test(amount);
    const dotDecimals = /\.\d\d$/.test(amount);
    if (commaDecimals) {
      amount = amount.replace(/\./g, '').replace(/,/g, '.');
    } else if (dotDecimals) {
      amount = amount.replace(/,/g, '');
    }
    amount = amount.replace(/[^-\d.]/g, '').replace(/^\./, '').replace(/\.$/, '');
    const parsed = Number(amount);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Parse VO cart-style property suffixes (`Value | $55.00` or `(+$445.00)`) into cents.
   * @param {Record<string, string> | null | undefined} properties
   * @returns {number}
   */
  static #optionPriceFromProperties(properties) {
    if (!properties || typeof properties !== 'object') return 0;
    let dollars = 0;
    for (const [key, value] of Object.entries(properties)) {
      const text = String(value == null ? '' : value);
      if (/^_(bcpo_)?(price|additional_price|option_price)$/i.test(key)) {
        const hidden = Number(text);
        if (Number.isFinite(hidden) && hidden !== 0) {
          dollars += Math.abs(hidden) >= 100 && Number.isInteger(hidden) ? hidden / 100 : hidden;
          continue;
        }
      }
      const match = text.match(/ \| ([^|]+)$/) || text.match(/\(\s*\+\s*([^)]+)\)\s*$/) || text.match(/\+\s*(\$[\d,.' ]+)/);
      if (!match) continue;
      dollars += this.#parseMoneyAmount(match[1]);
    }
    return this.#optionDollarsToCents(dollars);
  }

  /**
   * @param {unknown} data
   * @param {string | number | null | undefined} productId
   * @returns {object | null}
   */
  static #pickBcpoEntry(data, productId) {
    if (!data) return null;
    if (Array.isArray(data)) {
      if (productId) {
        for (const entry of data) {
          if (String(entry?.product_id) === String(productId)) return entry;
        }
      }
      return data[0] || null;
    }
    return data;
  }

  /**
   * @param {string | number | null | undefined} productId
   * @returns {object | null}
   */
  static #bcpoData(productId) {
    const fromCache = productId ? this.#bcpoByProductId.get(String(productId)) : null;
    if (fromCache) return this.#pickBcpoEntry(fromCache, productId);
    return this.#pickBcpoEntry(window.bcpo_data, productId);
  }

  /**
   * @param {string} text
   * @returns {unknown}
   */
  static #parseJsonPayload(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed || trimmed === 'null') return null;
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      const objectStart = trimmed.indexOf('{');
      const arrayStart = trimmed.indexOf('[');
      let start = -1;
      if (objectStart !== -1 && (arrayStart === -1 || objectStart < arrayStart)) start = objectStart;
      else if (arrayStart !== -1) start = arrayStart;
      if (start === -1) return null;
      const closer = trimmed[start] === '{' ? '}' : ']';
      const end = trimmed.lastIndexOf(closer);
      if (end <= start) return null;
      try {
        return JSON.parse(trimmed.slice(start, end + 1));
      } catch (parseError) {
        return null;
      }
    }
  }

  /**
   * Load VO option data for a product so cart imports can price selected options.
   * @param {string} handle
   * @param {string | number | null | undefined} productId
   * @returns {Promise<object | null>}
   */
  static async #loadBcpoData(handle, productId) {
    if (!handle) return productId ? this.#bcpoByProductId.get(String(productId)) || null : null;
    if (this.#bcpoByHandle.has(handle)) return this.#bcpoByHandle.get(handle);
    const request = (async () => {
      try {
        const response = await fetch(`${this.#routesRoot()}products/${encodeURIComponent(handle)}?view=apq-bcpo`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) return null;
        const data = this.#parseJsonPayload(await response.text());
        if (!data || (!data.virtual_options && !Array.isArray(data))) return null;
        if (productId) this.#bcpoByProductId.set(String(productId), data);
        if (data?.product_id) this.#bcpoByProductId.set(String(data.product_id), data);
        return data;
      } catch (error) {
        return null;
      }
    })();
    this.#bcpoByHandle.set(handle, request);
    const data = await request;
    this.#bcpoByHandle.set(handle, data);
    return data;
  }

  /**
   * Match selected line-item properties to `window.bcpo_data.virtual_options`.
   * @param {Record<string, string> | null | undefined} properties
   * @param {string | number | null | undefined} productId
   * @returns {number} cents
   */
  static #optionPriceFromBcpoData(properties, productId) {
    const options = this.#bcpoData(productId)?.virtual_options;
    if (!Array.isArray(options) || !properties) return 0;

    const selectedValues = new Set();
    for (const value of Object.values(properties)) {
      const text = String(value == null ? '' : value)
        .split(' | ')[0]
        .trim();
      if (!text) continue;
      for (const part of text.split(',')) {
        const trimmed = part.trim();
        if (trimmed) selectedValues.add(trimmed);
      }
    }
    if (!selectedValues.size) return 0;

    let dollars = 0;
    for (const option of options) {
      if (!Array.isArray(option?.values)) continue;
      for (const value of option.values) {
        const key = String(value?.key || '').trim();
        if (!key || !selectedValues.has(key)) continue;
        dollars += Number(value.price) || 0;
      }
    }
    return this.#optionDollarsToCents(dollars);
  }

  /**
   * Resolve VO option add-on cents from the live form, property suffixes, or bcpo_data.
   * @param {HTMLFormElement | null} form
   * @param {Record<string, string>} properties
   * @param {string | number | null | undefined} productId
   * @returns {number}
   */
  static #resolveOptionPrice(form, properties, productId) {
    const fromForm = form ? this.#optionPriceFromForm(form) : 0;
    if (fromForm) return fromForm;
    const fromProperties = this.#optionPriceFromProperties(properties);
    if (fromProperties) return fromProperties;
    return this.#optionPriceFromBcpoData(properties, productId);
  }

  /**
   * Variant price plus currently selected VO option add-ons.
   * @param {HTMLFormElement | null} form
   * @param {number} variantPrice
   * @param {string | number | null | undefined} productId
   * @returns {{ variantPrice: number, optionPrice: number, price: number }}
   */
  static displayedFormPrice(form, variantPrice, productId) {
    const parsed = this.#parseForm(form);
    const optionPrice = this.#resolveOptionPrice(form, parsed.properties, productId);
    const base = Number(variantPrice) || 0;
    return {
      variantPrice: base,
      optionPrice,
      price: base + optionPrice
    };
  }

  static formatMoney(cents) {
    return this.#formatMoney(cents);
  }

  static getQuote() {
    return this.#readStorage();
  }

  static getItems() {
    return this.getQuote().items;
  }

  static getItemCount() {
    let count = 0;
    for (const item of this.getItems()) {
      count += Number(item.quantity) || 0;
    }
    return count;
  }

  static getSubtotal() {
    let total = 0;
    for (const item of this.getItems()) {
      total += Number(item.final_line_price) || 0;
    }
    return total;
  }

  static hasProduct(productId) {
    if (!productId) return false;
    for (const item of this.getItems()) {
      if (String(item.product_id) === String(productId)) return true;
    }
    return false;
  }

  /**
   * Whether the exact variant + selected-options configuration is already a quote line.
   * @param {string | number} variantId
   * @param {Record<string, string> | null | undefined} properties
   * @returns {boolean}
   */
  static hasLine(variantId, properties) {
    if (!variantId) return false;
    const target = this.#fingerprint(variantId, properties);
    for (const item of this.getItems()) {
      if (this.#fingerprint(item.variant_id || item.id, item.properties) === target) return true;
    }
    return false;
  }

  static addItem(raw) {
    const incoming = this.#normalizeItem(raw);
    if (!incoming.variant_id) return null;
    const quote = this.getQuote();
    let saved = null;
    for (const item of quote.items) {
      if (!this.#sameLine(item, incoming)) continue;
      item.quantity += incoming.quantity;
      if (incoming.option_price && incoming.price) {
        item.price = incoming.price;
        item.option_price = incoming.option_price;
        item.original_price = incoming.original_price || item.original_price;
      }
      item.line_price = item.price * item.quantity;
      item.final_line_price = item.line_price;
      saved = item;
      break;
    }
    if (!saved) {
      quote.items.push(incoming);
      saved = incoming;
    }
    quote.started = true;
    this.#save(quote);
    this.markStarted(quote.route || 'add');
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), item: saved });
    this.#emit('rq:item-added', { item: saved });
    if (incoming.source === 'pdp') {
      window.requestAnimationFrame(() => this.show());
    }
    return saved;
  }

  static async addItemFromForm(form, meta = {}) {
    if (!form) throw new Error('A product form is required');
    const parsed = this.#parseForm(form);
    if (!parsed.variantId) throw new Error('Select a product variant');

    let product = meta.product || null;
    if (!product && meta.handle) {
      product = await this.#fetchProduct(meta.handle);
    }

    const variant = this.#findVariant(product, parsed.variantId);
    const variantPrice = Number(variant?.price || meta.price || 0) || 0;
    const optionPrice = this.#resolveOptionPrice(form, parsed.properties, product?.id || meta.product_id);
    const price = variantPrice + optionPrice;
    return this.addItem({
      id: parsed.variantId,
      variant_id: parsed.variantId,
      product_id: product?.id || meta.product_id,
      product_title: product?.title || meta.product_title || '',
      variant_title: variant?.public_title || variant?.title || meta.variant_title || '',
      title: product?.title || meta.product_title || '',
      quantity: parsed.quantity,
      price,
      original_price: variantPrice,
      option_price: optionPrice,
      image: this.#variantImage(variant, product) || meta.image || '',
      url: product?.url || meta.url || (product?.handle ? `/products/${product.handle}` : ''),
      sku: variant?.sku || '',
      handle: product?.handle || meta.handle || '',
      vendor: product?.vendor || '',
      properties: parsed.properties,
      source: meta.source || 'form'
    });
  }

  static async updateItemFromForm(key, form, meta = {}) {
    if (!key || !form) throw new Error('A quote line and product form are required');
    const quote = this.getQuote();
    const index = quote.items.findIndex((item) => item.key === key);
    if (index < 0) throw new Error('Quote line not found');

    const existing = quote.items[index];
    const parsed = this.#parseForm(form);
    if (!parsed.variantId) throw new Error('Select a product variant');

    let product = meta.product || null;
    if (!product && meta.handle) {
      product = await this.#fetchProduct(meta.handle);
    }

    const variant = this.#findVariant(product, parsed.variantId);
    const variantPrice = Number(variant?.price || meta.price || 0) || 0;
    const optionPrice = this.#resolveOptionPrice(form, parsed.properties, product?.id || meta.product_id);
    const price = variantPrice + optionPrice;
    const quantity = Math.max(1, Number(parsed.quantity) || Number(existing.quantity) || 1);
    const fingerprint = this.#fingerprint(parsed.variantId, parsed.properties);
    const duplicate = quote.items.find(
      (item, itemIndex) =>
        itemIndex !== index && this.#fingerprint(item.variant_id || item.id, item.properties) === fingerprint
    );

    let updated;
    if (duplicate) {
      duplicate.quantity += quantity;
      duplicate.line_price = duplicate.price * duplicate.quantity;
      duplicate.final_line_price = duplicate.line_price;
      quote.items.splice(index, 1);
      updated = duplicate;
    } else {
      updated = this.#normalizeItem({
        variant_id: parsed.variantId,
        product_id: product?.id || meta.product_id || existing.product_id,
        product_title: product?.title || meta.product_title || existing.product_title,
        variant_title: variant?.public_title || variant?.title || existing.variant_title,
        title: product?.title || meta.product_title || existing.product_title || existing.title,
        quantity,
        price,
        original_price: variantPrice,
        option_price: optionPrice,
        image: this.#variantImage(variant, product) || meta.image || existing.image,
        url: product?.url || meta.url || existing.url,
        sku: variant?.sku || existing.sku,
        handle: product?.handle || meta.handle || existing.handle,
        vendor: product?.vendor || existing.vendor,
        properties: parsed.properties,
        source: meta.source || existing.source,
        unavailable: false
      });
      quote.items[index] = updated;
    }

    quote.started = true;
    this.#save(quote);
    this.markStarted(quote.route || 'add');
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), item: updated });
    this.#emit('rq:item-updated', { item: updated, previousKey: key });
    return updated;
  }

  static removeItem(key) {
    if (!key) return false;
    const quote = this.getQuote();
    const nextItems = [];
    let removed = null;
    for (const item of quote.items) {
      if (item.key === key) removed = item;
      else nextItems.push(item);
    }
    if (!removed) return false;
    quote.items = nextItems;
    this.#save(quote);
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), item: removed });
    this.#emit('rq:item-removed', { item: removed });
    return true;
  }

  static updateQuantity(key, quantity) {
    const nextQuantity = Number(quantity);
    if (!key || !Number.isFinite(nextQuantity) || nextQuantity < 0) return null;
    if (nextQuantity === 0) {
      this.removeItem(key);
      return null;
    }
    const quote = this.getQuote();
    let updated = null;
    for (const item of quote.items) {
      if (item.key !== key) continue;
      item.quantity = nextQuantity;
      item.line_price = item.price * item.quantity;
      item.final_line_price = item.line_price;
      updated = item;
      break;
    }
    if (!updated) return null;
    this.#save(quote);
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), item: updated });
    return updated;
  }

  static updateItem(key, patch) {
    if (!key || !patch || typeof patch !== 'object') return null;
    const quote = this.getQuote();
    let updated = null;
    for (const item of quote.items) {
      if (item.key !== key) continue;
      Object.assign(item, patch);
      updated = item;
      break;
    }
    if (!updated) return null;
    this.#save(quote);
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), item: updated });
    return updated;
  }

  static clearItems() {
    const quote = this.getQuote();
    quote.items = [];
    this.#save(quote);
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote() });
  }

  static #removeStorageKey(storage, key) {
    try {
      storage.removeItem(key);
    } catch (error) {
      // Storage may be unavailable.
    }
  }

  static async importCart(cart, options = {}) {
    const replace = Boolean(options.replace);
    const snapshot = cart || (await this.#fetchCart());
    const quote = this.getQuote();
    const lines = snapshot.items || [];
    const handlesToLoad = [];
    const seenHandles = new Set();
    for (const line of lines) {
      if (!line.handle || seenHandles.has(line.handle)) continue;
      if (Number(line.option_price) || this.#optionPriceFromProperties(line.properties)) continue;
      seenHandles.add(line.handle);
      handlesToLoad.push({ handle: line.handle, productId: line.product_id });
    }
    if (handlesToLoad.length) {
      await Promise.all(handlesToLoad.map((item) => this.#loadBcpoData(item.handle, item.productId)));
    }
    const items = [];
    for (const line of lines) {
      const quantity = Math.max(1, Number(line.quantity) || 1);
      const variantPrice = Number(line.original_price || line.price) || 0;
      const optionPrice =
        Number(line.option_price) ||
        this.#optionPriceFromProperties(line.properties) ||
        this.#optionPriceFromBcpoData(line.properties, line.product_id);
      const price = variantPrice + optionPrice;
      const linePrice = optionPrice ? price * quantity : Number(line.final_line_price || line.line_price || price * quantity) || price * quantity;
      items.push(
        this.#normalizeItem({
          ...line,
          price,
          original_price: variantPrice,
          option_price: optionPrice,
          line_price: linePrice,
          final_line_price: linePrice,
          source: 'cart'
        })
      );
    }
    if (replace) {
      quote.items = items;
    } else {
      // The quote store is shared across tabs. Another tab may have added quote-only
      // items (e.g. a PDP "Add to quote") that never touched the real Shopify cart, so
      // a non-explicit re-sync (page resume, focus, Refresh) must not drop them - only
      // an explicit "use my cart" replacement is allowed to discard them.
      const preserved = quote.items.filter((item) => item.source !== 'cart');
      const preservedFingerprints = new Set(
        preserved.map((item) => this.#fingerprint(item.variant_id, item.properties))
      );
      const cartItems = items.filter(
        (item) => !preservedFingerprints.has(this.#fingerprint(item.variant_id, item.properties))
      );
      quote.items = [...cartItems, ...preserved];
    }
    quote.started = true;
    if (!quote.route) quote.route = 'cart';
    this.#save(quote);
    this.markStarted(quote.route || 'cart');
    this.syncTriggers();
    this.#emit('rq:change', { quote: this.getQuote(), source: 'cart' });
    return quote;
  }

  static toCartShape() {
    const items = this.getItems();
    return {
      items,
      item_count: this.getItemCount(),
      total_price: this.getSubtotal()
    };
  }

  static isStarted() {
    if (this.getQuote().started || this.getItems().length > 0) return true;
    try {
      if (sessionStorage.getItem(this.STARTED_KEY) === '1') return true;
    } catch (error) {
      // Fall through to cookie.
    }
    try {
      return /(?:^|;\s*)austpek-rfq-started=1(?:;|$)/.test(document.cookie);
    } catch (error) {
      return false;
    }
  }

  static markStarted(route) {
    const quote = this.getQuote();
    quote.started = true;
    if (route) quote.route = route;
    this.#save(quote);
    try {
      sessionStorage.setItem(this.STARTED_KEY, '1');
      if (route) sessionStorage.setItem(this.ROUTE_KEY, route);
    } catch (error) {
      // Storage may be unavailable.
    }
    try {
      document.cookie = 'austpek-rfq-started=1;path=/;max-age=604800;SameSite=Lax';
    } catch (error) {
      // Cookie may be unavailable.
    }
    this.syncTriggers();
    return quote;
  }

  static clearStarted() {
    const quote = this.getQuote();
    quote.started = false;
    quote.route = '';
    quote.panel = '';
    this.#save(quote);
    try {
      sessionStorage.removeItem(this.STARTED_KEY);
      sessionStorage.removeItem(this.ROUTE_KEY);
      sessionStorage.removeItem(this.PANEL_KEY);
    } catch (error) {
      // Ignore.
    }
    try {
      document.cookie = 'austpek-rfq-started=;path=/;max-age=0;SameSite=Lax';
    } catch (error) {
      // Ignore.
    }
    this.syncTriggers();
  }

  static clear() {
    this.#removeStorageKey(window.localStorage, this.STORAGE_KEY);
    this.#removeStorageKey(window.sessionStorage, this.STORAGE_KEY);
    this.#removeStorageKey(window.sessionStorage, this.STARTED_KEY);
    this.#removeStorageKey(window.sessionStorage, this.ROUTE_KEY);
    this.#removeStorageKey(window.sessionStorage, this.PANEL_KEY);
    try {
      document.cookie = 'austpek-rfq-started=;path=/;max-age=0;SameSite=Lax';
    } catch (error) {
      // Cookie may be unavailable.
    }
    this.close();
    this.refresh();
    this.#emit('rq:change', { quote: this.getQuote() });
    this.#emit('rq:cleared');
  }

  static getRoute() {
    try {
      return this.getQuote().route || sessionStorage.getItem(this.ROUTE_KEY) || 'add';
    } catch (error) {
      return this.getQuote().route || 'add';
    }
  }

  static getPanel() {
    try {
      return this.getQuote().panel || sessionStorage.getItem(this.PANEL_KEY) || 'add';
    } catch (error) {
      return this.getQuote().panel || 'add';
    }
  }

  static setPanel(panel) {
    const quote = this.getQuote();
    quote.panel = panel || '';
    this.#save(quote);
    try {
      if (panel) sessionStorage.setItem(this.PANEL_KEY, panel);
    } catch (error) {
      // Ignore.
    }
  }

  static syncTriggers() {
    const started = this.isStarted();
    const count = this.getItemCount();
    const manageMode = started && count > 0;
    const open = this.isOpen();

    // The header trigger stays visible on all breakpoints: before a quote is started
    // it links to the quote page, and it must survive a submission (which clears `started`).
    // Below 1000px the sticky bottom bar also appears in manage mode.
    document.documentElement.classList.toggle('rq-manage-mode', manageMode);
    document.querySelectorAll('[data-rq-trigger-wrap]').forEach((wrap) => {
      wrap.hidden = false;
    });

    document.querySelectorAll('[data-rq-trigger]').forEach((trigger) => {
      const requestLabel = trigger.dataset.rqLabelRequest || 'Request a quote';
      const manageLabel = trigger.dataset.rqLabelManage || 'Manage my quote';
      const text = manageMode ? manageLabel : requestLabel;
      const labelEl = trigger.querySelector('[data-rq-trigger-label]');
      if (labelEl) labelEl.textContent = text;
      trigger.setAttribute('aria-label', text);
      trigger.setAttribute('data-rq-mode', manageMode ? 'manage' : 'request');
      if (manageMode) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      } else {
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-expanded');
      }
    });

    document.querySelectorAll('[data-rq-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = !manageMode;
      badge.setAttribute('aria-hidden', manageMode ? 'false' : 'true');
    });

    document.querySelectorAll('[data-rq-count-label]').forEach((label) => {
      const one = label.dataset.rqLabelItemsOne || '__COUNT__ item';
      const other = label.dataset.rqLabelItemsOther || '__COUNT__ items';
      label.textContent = (count === 1 ? one : other).replace('__COUNT__', String(count));
      label.hidden = !manageMode;
    });

    document.querySelectorAll('[data-rq-count-sep]').forEach((sep) => {
      sep.hidden = !manageMode;
      sep.setAttribute('aria-hidden', manageMode ? 'false' : 'true');
    });

    document.querySelectorAll('[data-rq-trigger-chevron]').forEach((chevron) => {
      chevron.hidden = !manageMode;
    });

    document.querySelectorAll('[data-rq-account-dot]').forEach((dot) => {
      dot.hidden = !started;
      dot.setAttribute('aria-hidden', started ? 'false' : 'true');
    });

    document.querySelectorAll('[data-rq-account-indicator]').forEach((el) => {
      el.classList.toggle('has-rq-activity', started);
    });

    for (const button of document.querySelectorAll('[data-action="add-to-quote"]')) {
      const productId = button.getAttribute('data-product-id');
      const form = this.#quoteButtonForm(button);
      // A PDP variant switch only re-renders the price/SKU blocks, so the button keeps the
      // variant id Liquid rendered for the *initial* variant. Read the form's live `id` value
      // instead, and write it back, so both the saved-state check here and the buy-button code
      // that pairs buttons by `data-variant-id` follow the shopper's actual selection.
      const parsed = form ? this.#parseForm(form) : null;
      const variantId = (parsed && parsed.variantId) || Number(button.getAttribute('data-variant-id')) || 0;
      if (variantId) button.setAttribute('data-variant-id', String(variantId));
      const inQuote = variantId
        ? this.hasLine(variantId, parsed ? parsed.properties : {})
        : this.hasProduct(productId);
      button.dataset.inQuote = inQuote ? 'true' : 'false';
      if (button.getAttribute('aria-busy') === 'true') continue;
      const labelText = inQuote
        ? button.getAttribute('data-label-added') || 'In your quote'
        : button.getAttribute('data-label-add') || 'Add to quote';
      const labelEl = button.querySelector('[data-quote-label]');
      if (labelEl) {
        labelEl.textContent = labelText;
      } else {
        button.textContent = labelText;
      }
      const ariaText = inQuote
        ? button.getAttribute('data-aria-added') || labelText
        : button.getAttribute('data-aria-add') || labelText;
      button.setAttribute('aria-label', ariaText);
    }
  }

  /**
   * Coalesce the several signals a single variant switch produces (`change` on the id field,
   * `variant:change`, `product:rerender`, then the section swap itself) into one sync that
   * runs after the DOM has settled.
   */
  static #scheduleSync() {
    window.clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => this.syncTriggers(), 0);
  }

  /**
   * A variant switch re-renders the buy-buttons block from the server, which replaces the quote
   * button with markup that always says "Add to quote". Event timing alone cannot tell us when
   * that swap lands, so watch for the replacement node instead of guessing a delay.
   */
  static #observeQuoteButtons() {
    if (typeof MutationObserver !== 'function') return;
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          const isQuoteButton =
            (node.matches && node.matches('[data-action="add-to-quote"]')) ||
            (node.querySelector && node.querySelector('[data-action="add-to-quote"]'));
          if (!isQuoteButton) continue;
          this.#scheduleSync();
          return;
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  static syncCount() {
    this.syncTriggers();
    return this.getItemCount();
  }

  static isOpen() {
    return Boolean(this.#root && this.#root.classList.contains(this.OPEN_CLASS));
  }

  static #closeAccountPopover() {
    const popover = document.getElementById('account-popover');
    if (popover && popover.getAttribute('aria-hidden') === 'false') {
      document.dispatchEvent(new CustomEvent('popover:close'));
      popover.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[aria-controls="account-popover"]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
      });
    }
  }

  static #renderStep() {
    const root = this.#root;
    if (!root) return;
    const step = this.#panelToStep(this.getPanel());
    const stepOf = this.#label('labelStepOf').replace('__CURRENT__', String(step)).replace('__TOTAL__', '3');
    let reviewed = this.#label('labelStepProductsReviewed');
    let nextCopy = this.#label('labelContinueNextProject');
    if (step === 2) {
      reviewed = this.#label('labelStepProjectReviewed');
      nextCopy = this.#label('labelContinueNextContact');
    } else if (step === 3) {
      reviewed = this.#label('labelStepContactReviewed');
      nextCopy = this.#label('labelContinueNextSubmit');
    }

    const stepOfEl = root.querySelector('[data-rq-step-of]');
    if (stepOfEl) stepOfEl.textContent = stepOf;
    const reviewedEl = root.querySelector('[data-rq-step-reviewed]');
    if (reviewedEl) reviewedEl.textContent = reviewed;

    root.querySelectorAll('[data-rq-progress-seg]').forEach((seg) => {
      const index = Number(seg.getAttribute('data-rq-progress-seg'));
      seg.classList.toggle('is-active', index <= step);
    });

    const resumeNext = root.querySelector('[data-rq-resume-next]');
    if (resumeNext) resumeNext.textContent = nextCopy;

    const quoteUrl = root.dataset.quoteUrl || '/pages/request-a-quote';
    const route = this.getRoute();
    const reviewUrl = `${quoteUrl}?route=${encodeURIComponent(route === 'upload' ? 'build' : route)}`;
    root.querySelectorAll('[data-rq-continue], [data-rq-review]').forEach((link) => {
      link.setAttribute('href', reviewUrl);
    });
  }

  static #renderItems() {
    const root = this.#root;
    if (!root) return;
    const list = root.querySelector('[data-rq-items]');
    const listLabel = root.querySelector('[data-rq-list-label]');
    const subtotal = root.querySelector('[data-rq-subtotal]');
    if (!list) return;

    const items = this.getItems();
    const count = items.length;
    const countLabel =
      count === 1
        ? this.#label('labelProductsOne')
        : this.#label('labelProductsOther').replace('__COUNT__', String(count));

    if (listLabel) {
      listLabel.textContent = `${this.#label('labelInQuote')} · ${countLabel}`.toUpperCase();
    }
    if (subtotal) subtotal.textContent = this.#formatMoney(this.getSubtotal());

    if (!count) {
      list.innerHTML = `<li class="rq-popup__empty" data-rq-empty>${this.#escapeHtml(this.#label('labelEmpty'))}</li>`;
      return;
    }

    const rows = [];
    for (const item of items) {
      const meta = this.#itemMeta(item);
      const image = item.image
        ? `<img src="${this.#escapeHtml(item.image)}" alt="${this.#escapeHtml(item.product_title || '')}" width="48" height="48" loading="lazy">`
        : '';
      rows.push(`
        <li class="rq-popup__item">
          <div class="rq-popup__thumb">${image}</div>
          <div class="rq-popup__item-body">
            <div class="rq-popup__item-title-row">
              <p class="rq-popup__item-title">${this.#escapeHtml(item.product_title || '')}</p>
              <span class="rq-popup__check" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
                  <circle cx="7" cy="7" r="7" fill="currentColor"/>
                  <path d="M4 7.1 5.9 9l4.1-4.2" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
            ${meta ? `<p class="rq-popup__item-meta">${this.#escapeHtml(meta)}</p>` : ''}
          </div>
        </li>
      `);
    }
    list.innerHTML = rows.join('');
  }

  static refresh() {
    if (!this.#root) {
      this.syncTriggers();
      return;
    }
    this.#renderStep();
    this.#renderItems();
    this.syncTriggers();
  }

  static #focusableInPanel() {
    if (!this.#root) return [];
    const panel = this.#root.querySelector('[data-rq-panel]');
    if (!panel) return [];
    const nodes = [];
    for (const el of panel.querySelectorAll(this.FOCUSABLE)) {
      if (el.closest('[hidden]')) continue;
      if (el.getAttribute('tabindex') === '-1') continue;
      nodes.push(el);
    }
    return nodes;
  }

  static #visibleTrigger() {
    const triggers = document.querySelectorAll('[data-rq-trigger]');
    for (const trigger of triggers) {
      if (trigger.offsetParent !== null) return trigger;
    }
    return triggers[0] || null;
  }

  static positionPopup() {
    if (!this.#root) return;
    const trigger = this.#visibleTrigger();
    if (!trigger) return;

    const edge = 8;
    // Measure the sticky bar rather than the link inside it, so the panel clears
    // the bar's padding instead of sitting on top of it.
    const rect = (trigger.closest('[data-rq-sticky-quote]') || trigger).getBoundingClientRect();
    let right = Math.max(edge, window.innerWidth - rect.right);
    const maxWidth = Math.min(420, window.innerWidth - edge * 2);
    const left = window.innerWidth - right - maxWidth;
    if (left < edge) {
      right = Math.max(edge, window.innerWidth - maxWidth - edge);
    }
    this.#root.style.setProperty('--rq-popup-right', `${right}px`);

    // The sticky mobile bar sits at the bottom of the viewport, so the panel has to
    // grow upwards from it instead of dropping below it.
    const anchorBottom = rect.top > window.innerHeight / 2;
    this.#root.classList.toggle('rq-popup--anchor-bottom', anchorBottom);

    if (anchorBottom) {
      const bottom = Math.max(edge, window.innerHeight - rect.top + edge);
      this.#root.style.setProperty('--rq-popup-bottom', `${bottom}px`);
      this.#root.style.setProperty('--rq-popup-max-height', `${Math.max(200, rect.top - edge * 2)}px`);
      return;
    }

    const top = Math.max(edge, rect.bottom);
    this.#root.style.setProperty('--rq-popup-top', `${top}px`);
    this.#root.style.setProperty('--rq-popup-max-height', `${Math.max(200, window.innerHeight - top - edge)}px`);
  }

  /**
   * Close the mini cart before this popup takes focus. The cart drawer installs its own
   * `focusin` trap, so it has to finish closing (and release that trap) first — otherwise it
   * pulls focus straight back out of this dialog.
   */
  static #closeMiniCart() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart || miniCart.getAttribute('aria-hidden') !== 'false') return;
    const toggle = document.querySelector('[data-action="toggle-mini-cart"]');
    if (toggle) toggle.click();
  }

  /**
   * Keep focus inside the dialog. Anything that closes alongside this open (the cart drawer,
   * a popover) can restore focus to its own initiator a tick later, so re-assert once.
   */
  static #focusPanel() {
    const closeBtn = this.#root && this.#root.querySelector('.rq-popup__close');
    if (!closeBtn) return;
    closeBtn.focus();
    window.requestAnimationFrame(() => {
      if (!this.isOpen()) return;
      const panel = this.#root.querySelector('[data-rq-panel]');
      if (panel && panel.contains(document.activeElement)) return;
      closeBtn.focus();
    });
  }

  /**
   * @param {HTMLElement} [initiator] Control that opened the popup, focused again on close.
   */
  static show(initiator) {
    if (!this.#root || this.isOpen()) return;
    const active = document.activeElement;
    this.#lastFocus =
      initiator && initiator.isConnected && typeof initiator.focus === 'function'
        ? initiator
        : active && active !== document.body
        ? active
        : this.#visibleTrigger();
    this.#closeAccountPopover();
    this.#closeMiniCart();
    this.#root.classList.add(this.OPEN_CLASS);
    this.#root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rq-popup-open');
    document.querySelectorAll('[data-rq-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });
    this.positionPopup();
    this.refresh();
    this.positionPopup();
    this.#focusPanel();
  }

  static close() {
    if (!this.#root || !this.isOpen()) return;
    // Only hand focus back when it is still ours to hand back. A close triggered while focus
    // already moved on (a link in the panel navigating, another dialog opening) must not drag
    // the user back to the trigger.
    const panel = this.#root.querySelector('[data-rq-panel]');
    const hadFocus =
      this.#root === document.activeElement ||
      this.#root.contains(document.activeElement) ||
      !document.activeElement ||
      document.activeElement === document.body;
    this.#root.classList.remove(this.OPEN_CLASS);
    this.#root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rq-popup-open');
    document.querySelectorAll('[data-rq-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    if (!hadFocus) return;
    const stored = this.#lastFocus;
    const target =
      stored && stored !== document.body && stored.isConnected && typeof stored.focus === 'function'
        ? stored
        : this.#visibleTrigger();
    if (target && typeof target.focus === 'function') {
      target.focus();
    } else if (panel && panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  /**
   * @param {HTMLElement} [initiator]
   */
  static toggle(initiator) {
    if (this.isOpen()) this.close();
    else this.show(initiator);
  }

  static #onTriggerClick(event) {
    const trigger = event.target.closest('[data-rq-trigger]');
    if (!trigger) return;
    if (!this.isStarted() || this.getItemCount() <= 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.toggle(trigger);
  }

  static #onKeydown(event) {
    if (!this.isOpen()) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = this.#focusableInPanel();
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  static #bind() {
    if (this.#bound) return;
    this.#bound = true;
    document.addEventListener('click', (event) => this.#onTriggerClick(event));
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-rq-close]')) {
        event.preventDefault();
        this.close();
      }
    });
    document.addEventListener('keydown', (event) => this.#onKeydown(event));
    document.addEventListener('rq:change', () => {
      this.syncTriggers();
      if (this.isOpen()) this.refresh();
    });
    // `product:rerender` doesn't bubble, but the capture phase still reaches document.
    document.addEventListener('product:rerender', () => this.#scheduleSync(), true);
    // A variant switch or an option change alters which quote line the button would match, so
    // the saved-state label has to be recomputed against the new selection. `variant:change`
    // bubbles; the `change` delegate also covers the third-party option widget, which only
    // touches its own fields.
    document.addEventListener('variant:change', () => this.#scheduleSync());
    document.addEventListener('change', (event) => {
      const field = event.target;
      if (!field || typeof field.closest !== 'function') return;
      if (!field.closest('form[action*="/cart/add"], #bcpo, .bcpo-quickview')) return;
      this.#scheduleSync();
    });
    window.addEventListener('resize', () => {
      if (this.isOpen()) this.positionPopup();
    });
    window.addEventListener(
      'scroll',
      () => {
        if (this.isOpen()) this.positionPopup();
      },
      true
    );
  }

  static init() {
    this.#root = document.querySelector('[data-rq-popup]');
    if (this.#root && this.#root.parentElement !== document.body) {
      document.body.appendChild(this.#root);
    }
    this.#bind();
    this.#observeQuoteButtons();
    this.syncTriggers();
    if (this.#root) this.#renderStep();
    this.#emit('rq:ready');
  }
}

window.RQ = RQ;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => RQ.init());
} else {
  RQ.init();
}
